export const WORLD = {
  seed: 4481,
  groundSize: 160,
  groundSegments: 96,
  relief: 0.24,
  tufts: 3600,
  leaves: 120000,
  leafSize: [0.055, 0.145] as const,
  rocks: 150,
} as const;

export const RUNTIME = { lite: false };

/** everyone lands in the same world unless asked otherwise */
export const DEFAULT_WORLD = "explorer";

export const scatter = (full: number): number =>
  RUNTIME.lite ? Math.max(1, Math.round(full * 0.3)) : full;

export const RESOLUTION = {
  targetHeight: 224,
  minHeight: 96,
  maxHeight: 720,
} as const;

export const CAMERA = {
  azimuth: 45,
  elevation: 35.264,
  frustumHeight: 21,
  lookHeight: 1.05,
  distance: 140,
  damping: 0.09,
  minFrustum: 6.4,
  maxFrustum: 36,
} as const;

export const LIGHT = {
  azimuth: 78,
  elevation: 44,
  intensity: 4.4,
  ambient: 0.07,
  hemisphere: 0.13,
  shadowMapSize: 2048,
  shadowExtent: 20,
  shadowBias: -0.0009,
  shadowNormalBias: 0.012,
} as const;

export const DITHER = {
  levels: 3,
  cell: 1,
  exposure: 1,
  gamma: 1,
  contrast: 1.3,
  bayerSize: 8,
  fog: 1,
  shadowTint: 0x191c23,
  highlightTint: 0xf7f5ef,
} as const;

export const EXPLORED = {
  cells: 96,
  radius: 10.0,
  step: 0.5,
  worldSize: WORLD.groundSize,
} as const;

export const BLUR = {
  clearWorld: 9.4,
  blurWorld: 11.0,
  fogWorldStart: 6.6,
  fogWorldEnd: 9.4,
  blurStrength: 2.6,
  midMix: 0.6,
  mipScale: 1,
} as const;

export const DAY = {
  seconds: 720,
  start: 0.5,
} as const;

export const TORCH = {
  carryBelow: 0.9,
  fullBelow: 0.5,
  lightDistance: 46,
  lightNight: 42,
  lightDay: 0.4,
} as const;

export const FIRE = {
  spot: [2.9, 2.3] as const,
  reach: 2.6,
  fuel: 6,
  lightDistance: 52,
  lightDay: 0.7,
  lightNight: 46,
} as const;

export const CHEST = {
  spot: [-3.4, -8.6] as const,
  reach: 2.3,
  hold: 1,
  /** walk further than this from the chest itself and the lid shuts */
  leaveRadius: 5.5,
} as const;

/** the glasses widen the world radii the player can see clearly */
export const SIGHT = {
  bonus: 1.65,
  /** the reveal: the camera goes all the way out so you can see what you gained */
  zoomToMax: true,
  /** explored ground opens up further than the sharp radius does */
  exploreBonus: 1.95,
  seconds: 1.8,
} as const;

export const MOVE = {
  speed: 3.1,
  acceleration: 16,
  turnRate: 11,
} as const;

export const TINTS = {
  mono: { shadow: 0x191c23, highlight: 0xf7f5ef },
  blue: { shadow: 0x0a1220, highlight: 0xdae6f4 },
  red: { shadow: 0x1d0a0c, highlight: 0xf4e2dc },
  sepia: { shadow: 0x1b1206, highlight: 0xf6e7c9 },
  violet: { shadow: 0x120c22, highlight: 0xe7ddf6 },
  green: { shadow: 0x0a140f, highlight: 0xdcf0e3 },
} as const;

export const TOWN = {
  centre: [0, -6] as const,
  plazaRadius: 13,
  reach: 7,
  layout: [[0, -8]] as const,
  facing: [0] as const,
} as const;

export const NIGHT = {
  invertBelow: 0.6,
} as const;

export const TOAST = {
  hold: 3.4,
} as const;

export const PARTY = {
  reach: 2.6,
  hold: 1,
  inviteTimeout: 30,
  // same anchor the nameplate uses, so a peer hands over from one to the other in place
  headAnchor: 2.05,
  // shared by the in-world nameplate and the edge indicator so a peer hands over
  // from one to the other instead of showing both
  onScreen: 0.92,
} as const;

export const PALETTE = {
  scheme: "mono",
} as const;

export const ACCENTS = {
  none: 0x000000,
  red: 0xff3b2f,
  ember: 0xff7a2f,
  blue: 0x46a6ff,
  cyan: 0x5fe0e0,
  green: 0x74e07a,
  amber: 0xffc247,
  violet: 0xb07aff,
} as const;

export type TintName = keyof typeof TINTS;
export type AccentName = keyof typeof ACCENTS;
